import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import SectionLabel from "@/components/sections/SectionLabel";

const traditional = [
  "Executes predefined test cases",
  "Requires constant maintenance",
  "Limited regression coverage",
  "Doesn't understand code",
  "Finds expected bugs",
  "Human capacity is limited",
];

const liskov = [
  "Understands the codebase",
  "Decides what needs testing",
  "Explores like a real user",
  "Learns from previous failures",
  "Runs continuously",
  "Finds unknown regressions",
  "Works 24×7",
];

export default function Comparison() {
  return (
    <section
      id="comparison"
      data-testid="comparison-section"
      className="relative py-24 md:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <SectionLabel>The shift</SectionLabel>
          <h2 className="font-display font-semibold mt-4 text-balance text-4xl sm:text-5xl md:text-[56px] leading-[1.05] tracking-[-0.025em]">
            Traditional QA wasn&apos;t built for{" "}
            <span className="text-muted-foreground">AI-era software.</span>
          </h2>
          <p className="mt-5 text-muted-foreground max-w-xl">
            Software ships faster than ever. Manual scripts fall behind on day one.
            Liskov AI replaces the entire QA function with a code-aware engineer that thinks.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-14">
          {/* Traditional QA */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            data-testid="comparison-traditional-card"
            className="premium-card p-7 md:p-9 relative"
          >
            <div className="flex items-center justify-between mb-7">
              <span className="mono-label text-muted-foreground">Yesterday</span>
              <span className="mono-label text-muted-foreground line-through decoration-2 decoration-muted-foreground/40">
                Traditional QA
              </span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-7">
              Scripts that break.
            </h3>
            <ul className="space-y-3.5">
              {traditional.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm md:text-[15px]">
                  <span className="mt-1 h-5 w-5 inline-flex items-center justify-center rounded-full bg-foreground/[0.05] text-muted-foreground shrink-0">
                    <X className="h-3 w-3" />
                  </span>
                  <span className="text-muted-foreground line-through decoration-muted-foreground/30">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Liskov AI */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="comparison-liskov-card"
            className="premium-card p-7 md:p-9 relative overflow-hidden glow-ring"
            style={{
              background:
                "linear-gradient(180deg, hsl(var(--accent) / 0.06), hsl(var(--card)) 60%)",
            }}
          >
            <div className="flex items-center justify-between mb-7">
              <span className="mono-label text-[hsl(var(--accent))]">Today</span>
              <span className="mono-label text-foreground/70">Liskov AI · PQE</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-7">
              Engineering that thinks.
            </h3>
            <ul className="space-y-3.5">
              {liskov.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm md:text-[15px]">
                  <span className="mt-0.5 h-5 w-5 inline-flex items-center justify-center rounded-full bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] shrink-0">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-foreground/90">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
